import { CohortBaseDetail, CohortProgressDates, GetCurrentUser200 } from '@platform/client/schemas';
import { ClientProgramNames } from '../constants';

/**Helper that takes db value of corporate_client.url_param and returns the corresponding program string. */
export const getProgramNameFromParam = (clientName: string | undefined | null) => {
  let programName: string;
  switch (clientName?.toLowerCase()) {
    case 'sap':
      programName = ClientProgramNames.SAP;
      break;
    case 'f5':
      programName = ClientProgramNames.F5;
      break;
    case 'lumen':
      programName = ClientProgramNames.LUMEN;
      break;
    case 'ey':
      programName = ClientProgramNames.EY;
      break;
    case 'reckitt':
      programName = ClientProgramNames.RECKITT;
      break;
    case 'philips':
      programName = ClientProgramNames.PHILIPS;
      break;
    default:
      programName = ClientProgramNames.INDIVIDUAL;
      break;
  }
  return programName;
};

/** Helper function to determine if user role is inactive for conditional platform access */
export const isInactiveUser = (user: GetCurrentUser200 | undefined): boolean => {
  if (!user) return false;
  return user.accountType === 'experteer' && user.role === 'inactive';
};

export const isCorporateProNoCohort = (user: GetCurrentUser200 | undefined): boolean => {
  if (!user) return false;
  return user.accountType === 'experteer' && !!user.corporatePartner && !user.cohort;
};

export const isNonCorporatePro = (user: GetCurrentUser200 | undefined): boolean => {
  if (!user) return false;
  return user.accountType === 'experteer' && !user.corporatePartner;
};

type CohortDateStrings = {
  kickoff: string;
  engageStart: string;
  engageEnd: string;
  reflect: string;
  matchingStart: string;
  matchingEnd: string;
  matchingRange: string;
  planningStart: string;
  planningEnd: string;
  planningRange: string;
  actionStart: string;
  actionEnd: string;
  actionRange: string;
  wrapUp: string;
};

const formatShortDate = (isoDate: string): string =>
  new Date(isoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

type ProgressDateRanges = {
  matchingStart: string;
  matchingEnd: string;
  matchingRange: string;
  planningStart: string;
  planningEnd: string;
  planningRange: string;
  actionStart: string;
  actionEnd: string;
  actionRange: string;
  reflect: string;
};

/** Formats a cohort's derived milestone dates (see CohortProgressDates) into single dates and ranges. */
export const progressDatesToRanges = (progressDates: CohortProgressDates): ProgressDateRanges => {
  const matchingStart = formatShortDate(progressDates.matchingStart);
  const matchingEnd = formatShortDate(progressDates.matchingEnd);
  const planningStart = formatShortDate(progressDates.planningStart);
  const planningEnd = formatShortDate(progressDates.planningEnd);
  const actionStart = formatShortDate(progressDates.actionStart);
  const actionEnd = formatShortDate(progressDates.actionEnd);
  return {
    matchingStart,
    matchingEnd,
    matchingRange: `${matchingStart}-${matchingEnd}`,
    planningStart,
    planningEnd,
    planningRange: `${planningStart}-${planningEnd}`,
    actionStart,
    actionEnd,
    actionRange: `${actionStart}-${actionEnd}`,
    reflect: formatShortDate(progressDates.reflect),
  };
};

/** Returns the commonly used cohort dates formatted to strings representing single dates or ranges. */
export const cohortDatesToStrings = (cohort: CohortBaseDetail): CohortDateStrings => {
  const ranges = progressDatesToRanges(cohort.progressDates);
  return {
    kickoff: formatShortDate(cohort.kickoffDate),
    engageStart: formatShortDate(cohort.engagementStartDate),
    engageEnd: formatShortDate(cohort.engagementEndDate),
    ...ranges,
    wrapUp: formatShortDate(cohort.wrapupDate),
  };
};
